import { useNavState } from '@/providers/NavStateProvider';
import { useEffect, useRef } from 'react';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { Link } from 'react-router-dom';
import { useBase64FromFileInput } from '@/hooks/useBase64FromFileInput';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCreateImportedResources } from '@/hooks/api/2630_create_imported_resource';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useInvalidateImportedResources } from '@/hooks/api/2620_imported_resources';
import { ImportedResourcesElement } from '@/features/w/[identifier]/setting/data_import/ImportedResourcesElement';

export const DataImport = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('データインポート'), []);
  useEffect(() => setBreadcrumbs([]), []);

  const { _w_ } = useCurrentWorkspace();

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'data_import'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">データインポートの流れ</h5>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="timeline timeline-one-side" data-timeline-axis-style="dotted">
                  <div className="timeline-block mb-3">
                    <span className="timeline-step number">
                      <span className="fa-stack fa-lg" style={{ fontSize: '0.9rem' }}>
                        <i className="fa fa-circle fa-stack-2x text-dark"></i>
                        <span className="fa fa-inverse fa-stack-1x">1</span>
                      </span>
                    </span>
                    <div className="timeline-content">
                      <h6 className="text-dark font-weight-bold mb-0">チーム登録</h6>
                      <p className="text-sm mt-3 mb-2">
                        <Link to={`/w/${_w_!.identifier}/setting/teams`} className="text-decoration-underline">
                          チーム設定
                        </Link>
                        の「チーム新規登録」からチームを登録してください。
                        <br />
                        チームの詳細については
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-teams`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          こちら
                        </Link>
                        から確認できます。
                      </p>
                    </div>
                  </div>
                  <div className="timeline-block mb-3">
                    <span className="timeline-step number">
                      <span className="fa-stack fa-lg" style={{ fontSize: '0.9rem' }}>
                        <i className="fa fa-circle fa-stack-2x text-dark"></i>
                        <span className="fa fa-inverse fa-stack-1x">2</span>
                      </span>
                    </span>
                    <div className="timeline-content">
                      <h6 className="text-dark font-weight-bold mb-0">ユーザー登録</h6>
                      <p className="text-sm mt-3 mb-2">
                        <Link to={`/w/${_w_!.identifier}/setting/members`} className="text-decoration-underline">
                          スペースユーザー設定
                        </Link>
                        の「ユーザーの招待」からユーザーを追加登録できます。
                        <br />
                        ユーザーの招待時に設定する権限等は
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-user-permissions`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          こちら
                        </Link>
                        から確認できます。
                      </p>
                    </div>
                  </div>
                  <div className="timeline-block mb-3">
                    <span className="timeline-step number">
                      <span className="fa-stack fa-lg" style={{ fontSize: '0.9rem' }}>
                        <i className="fa fa-circle fa-stack-2x text-dark"></i>
                        <span className="fa fa-inverse fa-stack-1x">3</span>
                      </span>
                    </span>
                    <div className="timeline-content">
                      <h6 className="text-dark font-weight-bold mb-0">売上カテゴリ登録</h6>
                      <p className="text-sm mt-3 mb-2">
                        <Link to={`/w/${_w_!.identifier}/setting/products`} className="text-decoration-underline">
                          売上カテゴリ設定
                        </Link>
                        の「新規売上カテゴリ登録」からプロジェクト（売上）の管理に必要な管理項目を登録してください。
                        <br />
                        売上カテゴリについての詳細は
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-sales-category`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          こちら
                        </Link>
                        から確認できます。
                      </p>
                    </div>
                  </div>
                  <div className="timeline-block mb-3">
                    <span className="timeline-step number">
                      <span className="fa-stack fa-lg" style={{ fontSize: '0.9rem' }}>
                        <i className="fa fa-circle fa-stack-2x text-dark"></i>
                        <span className="fa fa-inverse fa-stack-1x">4</span>
                      </span>
                    </span>
                    <div className="timeline-content">
                      <h6 className="text-dark font-weight-bold mb-0">取引先登録</h6>
                      <p className="text-sm mt-3 mb-3">
                        <Link
                          to={`/w/${_w_!.identifier}/setting/business_partners`}
                          className="text-decoration-underline"
                        >
                          取引先設定
                        </Link>
                        の「新規取引先登録」からプロジェクト（売上）の管理に必要な管理項目を登録してください。
                        <br />
                        取引先についての詳細は
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-clients`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          こちら
                        </Link>
                        から確認できます。 また、
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-import`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          インポート機能
                        </Link>
                        で一括でアップロードを行うこともできます。
                      </p>
                      <div className="d-flex mb-2">
                        <a
                          href="https://storage.biz-node.app/import_templates/business_partner_import_format.csv"
                          className="btn btn-sm bg-gradient-secondary me-2"
                          target="_blank"
                          rel="noreferrer"
                          download={'business_partner_import_format.csv'}
                        >
                          テンプレートダウンロード
                        </a>
                        <CsvUploadButton context={'business_partner'} />
                      </div>
                    </div>
                  </div>
                  <div className="timeline-block mb-3">
                    <span className="timeline-step number">
                      <span className="fa-stack fa-lg" style={{ fontSize: '0.9rem' }}>
                        <i className="fa fa-circle fa-stack-2x text-dark"></i>
                        <span className="fa fa-inverse fa-stack-1x">5</span>
                      </span>
                    </span>
                    <div className="timeline-content">
                      <h6 className="text-dark font-weight-bold mb-0">プロジェクト（売上）の登録</h6>
                      <p className="text-sm mt-3 mb-3">
                        プロジェクト（売上）の各チームごとにプロジェクトを登録してください。
                        <br />
                        プロジェクト登録の詳細は
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-add-sales-project`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          こちら
                        </Link>
                        から確認できます。 また、
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-import`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          インポート機能
                        </Link>
                        で一括でアップロードを行うこともできます。
                      </p>
                      <div className="d-flex mb-2">
                        <a
                          href="https://storage.biz-node.app/import_templates/sales_import_format.csv"
                          className="btn btn-sm bg-gradient-secondary me-2"
                          target="_blank"
                          rel="noreferrer"
                          download={'sales_import_format.csv'}
                        >
                          テンプレートダウンロード
                        </a>
                        <CsvUploadButton context={'sales_order'} />
                      </div>
                    </div>
                  </div>
                  <div className="timeline-block mb-3">
                    <span className="timeline-step number">
                      <span className="fa-stack fa-lg" style={{ fontSize: '0.9rem' }}>
                        <i className="fa fa-circle fa-stack-2x text-dark"></i>
                        <span className="fa fa-inverse fa-stack-1x">6</span>
                      </span>
                    </span>
                    <div className="timeline-content">
                      <h6 className="text-dark font-weight-bold mb-0">プロジェクト（費用）の登録</h6>
                      <p className="text-sm mt-3 mb-3">
                        プロジェクト（費用）の各チームごとにプロジェクトを登録してください。
                        <br />
                        プロジェクト登録の詳細は
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-add-purchase-project`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          こちら
                        </Link>
                        から確認できます。 また、
                        <Link
                          to={`https://www.biz-node.app/rd/?wbfrd=manual-import`}
                          className="text-decoration-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          インポート機能
                        </Link>
                        で一括でアップロードを行うこともできます。
                      </p>
                      <div className="d-flex mb-2">
                        <a
                          href="https://storage.biz-node.app/import_templates/purchase_import_format.csv"
                          className="btn btn-sm bg-gradient-secondary me-2"
                          target="_blank"
                          rel="noreferrer"
                          download={'purchase_import_format.csv'}
                        >
                          テンプレートダウンロード
                        </a>
                        <CsvUploadButton context={'purchase_order'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <ImportedResourcesElement />
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

type FormInputs = {
  identifier: string;
  file: string;
  context: 'business_partner' | 'sales_order' | 'purchase_order';
};

const CsvUploadButton = ({ context }: { context: 'business_partner' | 'sales_order' | 'purchase_order' }) => {
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const { mutate, data, isPending, error } = useCreateImportedResources();
  const { invalidate } = useInvalidateImportedResources({ identifier: _w_!.identifier });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { base64Content, toBase64FromFile, fileName } = useBase64FromFileInput();
  const {
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      context: context,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (base64Content) {
      setValue('file', base64Content);
      handleSubmit(onSubmit)();
    }
  }, [base64Content]);

  useEffect(() => {
    if (data) {
      setValue('file', '');
      fileInputRef.current!.value = '';
      invalidate();
      toastSuccess('アップロードが完了しました');
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input type="hidden" {...register('file')} />
      <input
        id={`${context}-file-select`}
        type="file"
        onChange={toBase64FromFile}
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={'text/csv'}
      />
      <button
        type="button"
        className="btn btn-sm bg-gradient-dark"
        onClick={() => {
          document.getElementById(`${context}-file-select`)?.click();
        }}
      >
        アップロード
      </button>
    </form>
  );
};
